import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} viewport
 * @property {HTMLElement} [dots]
 * @property {HTMLElement[]} [slides]
 */

/**
 * Hero slideshow: cross-dissolve between slides, linear Ken Burns zoom on the
 * active slide, content fade-in and "ring" pagination dots. Autoplay pauses on
 * hover/focus/tab-hidden and is skipped under prefers-reduced-motion.
 *
 * @extends {Component<Refs>}
 */
export class SlHeroSliderComponent extends Component {
  requiredRefs = ['viewport'];

  /** @type {number} */
  #index = 0;
  /** @type {ReturnType<typeof setInterval> | null} */
  #timer = null;
  // Mouse hover suspends advancing without stopping the timer, so autoplay can
  // never get stuck paused (e.g. after a click leaves focus on a dot/arrow).
  #hovering = false;
  #wired = false;
  #controller = new AbortController();
  #pointerStartX = 0;
  #pointerStartY = 0;
  /** @type {number | null} */
  #pointerId = null;
  #dragging = false;
  #suppressClick = false;
  // Horizontal-scroll (trackpad two-finger swipe) navigation.
  #wheelAccum = 0;
  #wheelLocked = false;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #wheelTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#wire();
    this.#sync();
  }

  updatedCallback() {
    super.updatedCallback();
    // Re-sync after a Section Rendering API re-render (e.g. theme editor).
    this.#stop();
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stop();
    if (this.#wheelTimer) clearTimeout(this.#wheelTimer);
    this.#controller.abort();
  }

  /** One-time listener wiring for the lifetime of this element. */
  #wire() {
    if (this.#wired) return;
    this.#wired = true;
    const { signal } = this.#controller;
    // Hover tracking via pointer events filtered to pointerType === 'mouse'.
    // (Plain mouseenter/mouseleave is unsafe: touch fires a synthesized mouseenter
    // with NO matching mouseleave, which would pin #hovering=true and pause
    // autoplay forever after the first tap.) Keyboard focus is handled at tick time
    // via :focus-visible so a click/tap on a control never leaves autoplay stuck.
    this.addEventListener('pointerenter', this.#onPointerEnter, { signal });
    this.addEventListener('pointerleave', this.#onPointerLeave, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    // Pointer swipe (mouse drag + touch + pen) — bound to the host so it persists
    // across Section Rendering re-renders; events bubble up from the slides.
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.addEventListener('pointercancel', this.#onPointerCancel, { signal });
    // Cancel the click that a mouse drag would otherwise fire on a slide link/button.
    this.addEventListener('click', this.#onClickCapture, { signal, capture: true });
    // Trackpad horizontal scroll navigates slides. passive:false so we can stop the
    // page from horizontal-scrolling while a horizontal gesture drives the carousel.
    this.addEventListener('wheel', this.#onWheel, { signal, passive: false });

    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
  }

  /** Idempotent: (re)build dots and restart autoplay from current DOM. */
  #sync() {
    const slides = this.#slides;
    if (slides.length === 0) return;

    this.classList.add('is-ready');
    this.classList.toggle('is-single', slides.length <= 1);
    this.#index = slides.findIndex((slide) => slide.classList.contains('is-active'));
    if (this.#index < 0) this.#index = 0;

    this.#buildDots();
    this.#render();
    this.#start();
  }

  /** @returns {HTMLElement[]} */
  get #slides() {
    return /** @type {HTMLElement[]} */ (this.refs.slides ?? []);
  }

  get #count() {
    return this.#slides.length;
  }

  get #autoplay() {
    return this.dataset.autoplay === 'true';
  }

  get #delay() {
    return Number.parseInt(this.dataset.autoplayDelay ?? '9000', 10) || 9000;
  }

  get #transition() {
    return Number.parseInt(this.dataset.transition ?? '1200', 10) || 1200;
  }

  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #buildDots() {
    const container = this.refs.dots;
    if (!container) return;

    container.replaceChildren();
    this.#slides.forEach((slide, i) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'sl-hero-slider__dot';
      dot.dataset.index = String(i);
      dot.setAttribute('aria-label', slide.dataset.slideLabel || this.#slideStatusLabel(i));
      dot.setAttribute('aria-current', i === this.#index ? 'true' : 'false');

      const inner = document.createElement('span');
      inner.className = 'sl-hero-slider__dot-inner';
      dot.append(inner);
      container.append(dot);
    });

    // Delegate clicks once per container element.
    if (container.dataset.bound !== 'true') {
      container.dataset.bound = 'true';
      container.addEventListener('click', this.#onDotClick, { signal: this.#controller.signal });
    }
  }

  /**
   * Localized "Slide N of M" fallback label, used when a slide has no heading.
   * Built from the server-rendered template on `data-slide-status`.
   * @param {number} i
   * @returns {string}
   */
  #slideStatusLabel(i) {
    const template = this.dataset.slideStatus;
    if (!template) return `Slide ${i + 1}`;
    return template.replace('sl_index', String(i + 1)).replace('sl_length', String(this.#count));
  }

  /** @param {Event} event */
  #onDotClick = (event) => {
    try {
      const target = /** @type {HTMLElement} */ (event.target);
      const dot = target.closest('[data-index]');
      if (!(dot instanceof HTMLElement)) return;
      this.select(Number.parseInt(dot.dataset.index ?? '0', 10));
    } catch (error) {
      console.error('SlHeroSliderComponent.#onDotClick failed:', error);
    }
  };

  /**
   * Navigate to a slide and restart the autoplay countdown.
   * @param {number} next
   */
  select(next) {
    const count = this.#count;
    if (count <= 1) return;
    next = ((next % count) + count) % count;
    if (next === this.#index) return;

    const slides = this.#slides;
    const leaving = slides[this.#index];
    this.#index = next;
    this.#render();

    // Keep the outgoing slide zooming through the dissolve, then reset it so a
    // later re-entry restarts cleanly.
    setTimeout(() => {
      if (leaving && !leaving.classList.contains('is-active')) {
        leaving.classList.remove('is-zooming');
      }
    }, this.#transition);

    if (this.#autoplay) this.#start();
  }

  next() {
    try {
      this.select(this.#index + 1);
    } catch (error) {
      console.error('SlHeroSliderComponent.next failed:', error);
    }
  }

  previous() {
    try {
      this.select(this.#index - 1);
    } catch (error) {
      console.error('SlHeroSliderComponent.previous failed:', error);
    }
  }

  #render() {
    const slides = this.#slides;
    slides.forEach((slide, i) => {
      const active = i === this.#index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      slide.toggleAttribute('inert', !active);

      if (active) {
        // Restart the Ken Burns animation from scale(1).
        slide.classList.remove('is-zooming');
        void slide.offsetWidth;
        slide.classList.add('is-zooming');
      }
    });

    const dots = this.refs.dots?.children;
    if (dots) {
      for (let i = 0; i < dots.length; i++) {
        dots[i]?.setAttribute('aria-current', i === this.#index ? 'true' : 'false');
      }
    }
  }

  #start() {
    this.#stop();
    if (!this.#autoplay || this.#reducedMotion || this.#count <= 1) return;
    // The timer stays alive; each tick checks whether it should advance. This way
    // hovering or keyboard interaction only suspends advancing — it never leaves
    // autoplay permanently stopped the way a sticky focus-pause would.
    this.#timer = setInterval(() => {
      if (this.#shouldHold) return;
      this.next();
    }, this.#delay);
  }

  /** Whether autoplay should skip advancing on this tick. */
  get #shouldHold() {
    // :focus-visible matches keyboard focus only — a mouse/touch click on a dot or
    // arrow focuses it but does NOT match, so manual navigation never gets stuck.
    return this.#hovering || document.hidden || this.querySelector(':focus-visible') != null;
  }

  #stop() {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  /** @param {PointerEvent} event */
  #onPointerEnter = (event) => {
    // Mouse only — ignore touch/pen so a tap can't leave #hovering stuck true.
    if (event.pointerType === 'mouse') this.#hovering = true;
  };

  /** @param {PointerEvent} event */
  #onPointerLeave = (event) => {
    if (event.pointerType === 'mouse') this.#hovering = false;
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') {
      this.previous();
    } else if (event.key === 'ArrowRight') {
      this.next();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (this.#count <= 1) return;
    // Clear any stale suppress flag from a prior swipe that produced no click
    // (touch swipes usually don't), so it can't eat the next legitimate tap.
    this.#suppressClick = false;
    this.#dragging = true;
    this.#pointerId = event.pointerId;
    this.#pointerStartX = event.clientX;
    this.#pointerStartY = event.clientY;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    if (!this.#dragging || event.pointerId !== this.#pointerId) return;
    this.#dragging = false;
    this.#pointerId = null;
    const deltaX = event.clientX - this.#pointerStartX;
    const deltaY = event.clientY - this.#pointerStartY;
    // Horizontal swipe past the threshold, ignoring mostly-vertical scrolls.
    if (Math.abs(deltaX) > 40 && Math.abs(deltaX) > Math.abs(deltaY)) {
      this.#suppressClick = true;
      if (deltaX < 0) this.next();
      else this.previous();
    }
  };

  #onPointerCancel = () => {
    this.#dragging = false;
    this.#pointerId = null;
  };

  /** @param {MouseEvent} event */
  #onClickCapture = (event) => {
    if (!this.#suppressClick) return;
    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Trackpad horizontal scroll (two-finger swipe left/right) advances slides,
   * mirroring the pointer-drag swipe. A vertical-dominant wheel is left untouched
   * so the page scrolls normally. A short lock keeps one gesture to one slide so
   * inertial/momentum events don't skip several.
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    if (this.#count <= 1) return;
    // Only react to a clearly horizontal gesture.
    if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    event.preventDefault();
    if (this.#wheelLocked) return;

    this.#wheelAccum += event.deltaX;
    if (Math.abs(this.#wheelAccum) < 40) return;

    if (this.#wheelAccum > 0) this.next();
    else this.previous();

    this.#wheelAccum = 0;
    this.#wheelLocked = true;
    this.#wheelTimer = setTimeout(() => {
      this.#wheelLocked = false;
      this.#wheelAccum = 0;
    }, this.#transition);
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const detail = /** @type {CustomEvent} */ (event).detail ?? {};
    const blockId = detail.blockId;
    const index = this.#slides.findIndex((slide) => slide.dataset.blockId === blockId);
    if (index >= 0) this.select(index);
    // Hold on the selected slide in the theme editor instead of auto-advancing.
    this.#stop();
  };
}

if (!customElements.get('sl-hero-slider-component')) {
  customElements.define('sl-hero-slider-component', SlHeroSliderComponent);
}
